import { useCallback, useState } from 'react';

import styled from '@emotion/styled';
import { Grid } from '@kc-console/common-ui';
import {
  LayoutType,
  PageLayoutProvider,
  SplitPanelId,
  SplitPanelInnerId,
} from '@kc-console/context';

import { DEFAULT_LAYOUT_STYLE } from '../../constants/layout';
import { useDraggablePanelLayoutV2 } from '../../hooks/useDraggablePanelLayoutV2';
import { usePanelViewport } from '../../hooks/usePanelViewport';
import { BannerRenderer } from '../BannerRenderer';
import { ScrollObserverRootWrapper } from '../ScrollObserverWrapper';
import { PanelPortal } from './PanelLayout';
import {
  LayoutBannerContainerWrapper,
  LayoutBodyContainerWrapper,
  LayoutContentSectionWrapper,
  LayoutHeaderContainerWrapper,
  LayoutSplitSectionWrapper,
  LayoutWrapper,
} from './Wrapper';

import type {
  DefaultComponentProps,
  DetailSectionProps,
  DetailSplitSectionWrapperProps,
} from './types';

/**
 * DetailLayout
 *
 * - usePanelViewport: PanelNavigation 직접 구독
 * - useDraggablePanelLayoutV2: usePanelPreferenceStore 기반 리사이즈
 * - flexDirection="row": 우측 패널 레이아웃
 */
export default function DetailLayout(props: DefaultComponentProps) {
  const { className, children } = props;
  return (
    <PageLayoutProvider layoutType={LayoutType.detail}>
      <LayoutWrapper className={className} flexDirection="row">
        {children}
        <DetailPanelViewport />
      </LayoutWrapper>
    </PageLayoutProvider>
  );
}

export const DetailSection = (props: DetailSectionProps) => {
  const { headerView, children } = props;

  return (
    <>
      <ScrollObserverRootWrapper
        rootComponent={
          <DetailContentSection>
            <DetailBannerContainer>
              <BannerRenderer />
            </DetailBannerContainer>
            <DetailHeaderContainer>{headerView}</DetailHeaderContainer>
            <DetailBodyContainer>{children}</DetailBodyContainer>
          </DetailContentSection>
        }
      />
      <DetailSplitSection />
    </>
  );
};

/**
 * V2 패널 뷰포트 - PanelNavigation 직접 구독
 */
const DetailPanelViewport = () => {
  const { visible, targetPanel } = usePanelViewport();
  const [localVisible, setLocalVisible] = useState(visible);

  const transitionendCallback = useCallback(() => {
    setLocalVisible(visible);
  }, [visible]);

  return (
    <PanelPortal
      targetPanel={targetPanel}
      localVisible={localVisible || visible}
      transitionendCallback={transitionendCallback}
    />
  );
};

const DetailContentSection = styled(LayoutContentSectionWrapper)`
  // NOTE. ScrollObserverEntry Element의 offsetTop을 위해 position relative 적용
  position: relative;
  overflow: auto;
`;

const DetailHeaderContainer = styled(LayoutHeaderContainerWrapper)`
  min-width: ${DEFAULT_LAYOUT_STYLE.MIN_WIDTH}px;
`;

const DetailBannerContainer = styled(LayoutBannerContainerWrapper)`
  min-width: ${DEFAULT_LAYOUT_STYLE.MIN_WIDTH}px;
`;

const DetailBodyContainer = styled(LayoutBodyContainerWrapper)`
  padding: 0 32px 32px 32px;
  min-width: ${DEFAULT_LAYOUT_STYLE.MIN_WIDTH}px;
`;

/**
 * V2 Split Section - useDraggablePanelLayoutV2 기반
 */
const DetailSplitSection = () => {
  const { visible } = usePanelViewport();
  const { dragBarRef, targetPanelSize, handleMouseDown } = useDraggablePanelLayoutV2({
    layoutType: 'detail',
    visible,
  });

  return (
    <DetailSplitSectionWrapper
      ref={dragBarRef}
      id={SplitPanelId}
      container
      flexDirection="row"
      visible={visible}
      width={targetPanelSize}
    >
      <DetailSplitSectionInnerDividerWrapper
        container
        justifyContent="end"
        alignItems="center"
        onMouseDown={handleMouseDown}
      >
        <DetailSplitSectionBar />
      </DetailSplitSectionInnerDividerWrapper>
      <DetailSplitSectionInnerWrapper id={SplitPanelInnerId} container />
    </DetailSplitSectionWrapper>
  );
};

const DetailSplitSectionWrapper = styled(LayoutSplitSectionWrapper, {
  shouldForwardProp: prop => prop !== 'visible',
})<DetailSplitSectionWrapperProps>`
  margin-left: ${({ visible }) => (visible ? '8px' : 0)};
  width: ${({ visible, width }) => (visible ? `${width}px` : 0)};
  height: 100%;
  flex-wrap: nowrap;
  transition:
    margin-left 0.3s,
    width 0.3s;
`;

const DetailSplitSectionInnerDividerWrapper = styled(Grid)`
  width: 11px;
  height: 100%;
  cursor: ew-resize;
`;

const DetailSplitSectionBar = styled(Grid)`
  width: 3px;
  height: 24px;
  background-color: ${({ theme }) => theme.color['icon/weakest/default']};
`;

const DetailSplitSectionInnerWrapper = styled(Grid)`
  width: 100%;
  height: 100%;
`;
